import React, { Dispatch, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Grid, Icon, Message } from "semantic-ui-react";
import { getCurrentUsersPatterns } from "../../../actions/patternActions";
import Spinner from "../../../components/Spinner/Spinner";
import { PatternDifficulty } from "../../../types/siteTypes";
import { patternListSelector } from "../../../utils/selectors";
import FilterSidebar from "../../PatternsList/FilterSidebar";
import PatternBox from "../../Patterns/PatternBox";
import styles from "./PatternList.module.scss";

const normalizeDifficulty = (value: unknown): PatternDifficulty => {
	const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
	if (normalized === "easy" || normalized === "hard" || normalized === "medium") {
		return normalized;
	}
	return "medium";
};

const PatternList: React.FC<any> = ({
	patternList: { patterns, loading, error },
	getProducts,
}) => {
	const intl = useIntl();
	const searchPlaceholder = intl.formatMessage({
		id: "dashboard.patterns.search.placeholder",
		defaultMessage: "Search my patterns",
	});
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
	const [selectedClients, setSelectedClients] = useState<string[]>([]);
	const [selectedFabrics, setSelectedFabrics] = useState<string[]>([]);
	const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
	const [selectedDifficulty, setSelectedDifficulty] =
		useState<PatternDifficulty | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

	useEffect(() => {
		getProducts();
	}, [getProducts]);

	const clearFilters = () => {
		setSelectedCategories([]);
		setSelectedClients([]);
		setSelectedFabrics([]);
		setSelectedSizes([]);
		setSelectedDifficulty(null);
		setSearchQuery("");
	};

	const hasActiveFilters =
		selectedCategories.length > 0 ||
		selectedClients.length > 0 ||
		selectedFabrics.length > 0 ||
		selectedSizes.length > 0 ||
		!!selectedDifficulty ||
		searchQuery.trim().length > 0;

	const patternsList = useMemo(
		() => (Array.isArray(patterns) ? patterns : []),
		[patterns],
	);

	const filteredPatterns = useMemo(() => {
		const hasAny = (source: string[] = [], selected: string[]) =>
			selected.length === 0 || selected.some((value) => source.includes(value));

		return patternsList.filter((pattern: any) => {
			const patternCategories = Array.isArray(pattern.categories)
				? pattern.categories
				: [];
			const patternClients = Array.isArray(pattern.clients) ? pattern.clients : [];
			const patternFabrics = Array.isArray(pattern.fabrics) ? pattern.fabrics : [];
			const patternSizes = Array.isArray(pattern.sizes) ? pattern.sizes : [];
			const patternDifficulty = normalizeDifficulty(pattern.difficulty);
			const normalizedSearch = searchQuery.trim().toLowerCase();
			const matchesSearch =
				normalizedSearch.length === 0 ||
				String(pattern.name || "").toLowerCase().includes(normalizedSearch);
			const matchesDifficulty =
				!selectedDifficulty || patternDifficulty === selectedDifficulty;

			return (
				matchesSearch &&
				matchesDifficulty &&
				hasAny(patternCategories, selectedCategories) &&
				hasAny(patternClients, selectedClients) &&
				hasAny(patternFabrics, selectedFabrics) &&
				hasAny(patternSizes, selectedSizes)
			);
		});
	}, [
		patternsList,
		selectedCategories,
		selectedClients,
		selectedFabrics,
		selectedSizes,
		selectedDifficulty,
		searchQuery,
	]);

	const hasPatterns = patternsList.length > 0;
	const resultsCount = filteredPatterns.length;

	return (
		<Grid>
			<Grid.Column width={4} only="computer">
				<FilterSidebar
					selectedCategories={selectedCategories}
					setSelectedCategories={setSelectedCategories}
					selectedClients={selectedClients}
					setSelectedClients={setSelectedClients}
					selectedFabrics={selectedFabrics}
					setSelectedFabrics={setSelectedFabrics}
					selectedSizes={selectedSizes}
					setSelectedSizes={setSelectedSizes}
					selectedDifficulty={selectedDifficulty}
					setSelectedDifficulty={setSelectedDifficulty}
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					searchPlaceholder={searchPlaceholder}
				/>
			</Grid.Column>
			<Grid.Column
				mobile={16}
				tablet={16}
				only="tablet mobile"
				className={styles.mobileFiltersColumn}
			>
				<div className={styles.mobileFiltersHeader}>
					<button
						type="button"
						className={styles.mobileFiltersToggle}
						onClick={() => setMobileFiltersOpen((prev) => !prev)}
					>
						<span className={styles.mobileFiltersToggleLabel}>
							<Icon name={mobileFiltersOpen ? "angle up" : "filter"} />
							<FormattedMessage
								id="patterns.filters.title"
								defaultMessage="Filters"
							/>
						</span>
					</button>
					<button
						type="button"
						className={styles.mobileFiltersClear}
						onClick={clearFilters}
						disabled={!hasActiveFilters}
					>
						<FormattedMessage
							id="patterns.filters.clearFilters"
							defaultMessage="Clear filters"
						/>
					</button>
				</div>
				<div className={styles.mobileFiltersResults}>
					<span className={styles.mobileFiltersCount}>
						<FormattedMessage
							id="patterns.results.short"
							defaultMessage="{count} results"
							values={{ count: resultsCount }}
						/>
					</span>
				</div>
				{mobileFiltersOpen && (
					<div className={styles.mobileFiltersPanel}>
						<FilterSidebar
							selectedCategories={selectedCategories}
							setSelectedCategories={setSelectedCategories}
							selectedClients={selectedClients}
							setSelectedClients={setSelectedClients}
							selectedFabrics={selectedFabrics}
							setSelectedFabrics={setSelectedFabrics}
							selectedSizes={selectedSizes}
							setSelectedSizes={setSelectedSizes}
							selectedDifficulty={selectedDifficulty}
							setSelectedDifficulty={setSelectedDifficulty}
							searchValue={searchQuery}
							onSearchChange={setSearchQuery}
							searchPlaceholder={searchPlaceholder}
							resultsCount={resultsCount}
							isSticky={false}
							showHeader={false}
						/>
					</div>
				)}
			</Grid.Column>

			<Grid.Column mobile={16} tablet={16} computer={12}>
				<main className={styles.mainSection}>
					{!loading && !error && hasPatterns && (
						<div className={styles.resultsRow}>
							<FormattedMessage
								id="patterns.results.short"
								defaultMessage="{count} results"
								values={{ count: resultsCount }}
							/>
						</div>
					)}
					{loading ? (
						<Spinner />
					) : error && !hasPatterns ? (
						<Message negative>
							<Message.Header>
								<FormattedMessage
									id="dashboard.patterns.error.title"
									defaultMessage="Failed to load your patterns"
								/>
							</Message.Header>
							<p>{error}</p>
						</Message>
					) : !hasPatterns ? (
						<Message info>
							<Message.Header>
								<FormattedMessage
									id="dashboard.patterns.empty.title"
									defaultMessage="You have no patterns yet"
								/>
							</Message.Header>
							<p>
								<FormattedMessage
									id="dashboard.patterns.empty.message"
									defaultMessage="Add your first pattern to have it shown here."
								/>
							</p>
						</Message>
					) : resultsCount === 0 ? (
						<Message info>
							<Message.Header>
								<FormattedMessage
									id="dashboard.patterns.filtered.empty.title"
									defaultMessage="No patterns match your filters"
								/>
							</Message.Header>
							<p>
								<FormattedMessage
									id="dashboard.patterns.filtered.empty.message"
									defaultMessage="Change filters to see your patterns."
								/>
							</p>
						</Message>
					) : (
						<Grid>
							{filteredPatterns.map((pattern: any) => (
								<Grid.Column
									mobile={16}
									tablet={8}
									computer={4}
									key={pattern._id}
								>
									<PatternBox pattern={pattern} variant="owner" />
								</Grid.Column>
							))}
						</Grid>
					)}
				</main>
			</Grid.Column>
		</Grid>
	);
};

const mapStateToProps = (state: any) => ({
	patternList: patternListSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
	getProducts: () => dispatch(getCurrentUsersPatterns()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatternList);
